import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import Select from 'react-select';
import './Navigation.css';
import { trackPromise } from 'react-promise-tracker';
import AuthContext from "../context/AuthProvider";
import axiosInstance from '../api/axios';
import makeAnimated from 'react-select/animated';
import { BsFilterCircleFill, BsFilter, BsFillTrashFill, BsFileEarmarkBarGraphFill } from "react-icons/bs";
import TablesNoData from './TablesNoData';
import SimpleStudytData from './SimpleStudyData';
import EvolutiveStudytData from './EvolutiveStudyData';
import { DEBUG } from '../config';

const colourStyles = {
    control: base => ({
        ...base,
        fontSize: '14px'
    }),
    container: base => ({
        ...base,
        flex: 1
    }),
    menu: base => ({
        ...base,
        fontSize: '14px'
    }),
    placeholder: (styles) => {
        return {
            ...styles,
            fontSize: '14px',
        }
    },
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
        return {
            ...styles,
            fontSize: '14px',
            backgroundColor: isFocused ? "#CACACA" : null,
            color: "#333333",
            width: "auto"
        };
    }
};


const Navigation = () => {
    const authContext = useContext(AuthContext);
    const { auth } = useContext(AuthContext);
    const username = auth.user;
    const token = auth.token;
    const [studiesOptions, setStudiesOptions] = useState([]);
    const [selectedStudy, setSelectedStudy] = useState(null);
    const [studyName, setStudyName] = useState(null);
    const [wavesOptions, setWavesOptions] = useState([]);
    const wavesDefaultOption = { label: "Selecione a vaga", value: "" }
    const [selectedWave, setSelectedWave] = useState(wavesDefaultOption);
    const [waveName, setWaveName] = useState(null);
    const [studyTypes, setStudyTypes] = useState([]);
    const [studyTypesOptions, setStudyTypesOptions] = useState([]);
    const studyTypesDefaultOption = { label: "Selecione a tipo", value: "" }
    const [selectedStudyType, setSelectedStudyType] = useState(studyTypesDefaultOption);
    const [isEvolutivo, setIsEvolutivo] = useState(null);
    const [themes, setThemes] = useState([]);
    const [themesOptions, setThemesOptions] = useState([]);
    const themesDefaultOption = { label: "Selecione o tema", value: "" }
    const [selectedTheme, setSelectedTheme] = useState(themesDefaultOption);
    const [hasThemes, setHasThemes] = useState(false);
    const [lines, setLines] = useState([]);
    const [lineName, setLineName] = useState(null);
    const [linesOptions, setLinesOptions] = useState([]);
    const linesDefaultOption = { label: "Selecione a variável em linha", value: "" }
    const [selectedLine, setSelectedLine] = useState(linesDefaultOption);
    const [columns, setColumns] = useState([]);
    const [columnName, setColumnName] = useState(null);
    const [columnLabel, setColumnLabel] = useState(null);
    const [columnsOptions, setColumnsOptions] = useState([]);
    const columnsDefaultOption = { label: "Selecione a variável em coluna", value: "" }
    const [selectedColumn, setSelectedColumn] = useState(columnsDefaultOption);
    const [vagasEvolutivos, setVagasEvolutivos] = useState([]);
    const [vagasEvolutivosOptions, setVagasEvolutivosOptions] = useState([]);
    const [selectedVagasEvolutivo, setSelectedVagasEvolutivo] = useState([]);
    const [totalVagasEvolutivo, setTotalVagasEvolutivo] = useState(0);
    const maxVagasEvolutivos = 7;
    const animatedComponents = makeAnimated();
    const [showFilter, setShowFilter] = useState(false);
    const [aggregators, setAggregators] = useState([]);
    const [aggregatorsOptions, setAggregatorsOptions] = useState([]);
    const aggregatorsDefaultOption = { label: "Selecione a variável do filtro", value: "" }
    const [selectedAggregator, setSelectedAggregator] = useState(aggregatorsDefaultOption);
    const [aggregatorLines, setAggregatorLines] = useState([]);
    const [aggregatorLineOptions, setAggregatorsLineOptions] = useState([]);
    const [showButtons, setShowButtons] = useState(false);
    const [filters, setFilters] = useState([]);
    const [showSimpleStudy, setShowSimpleStudy] = useState(false);
    const [showEvolutiveStudy, setShowEvolutiveStudy] = useState(false);
    const [enableVaga, setEnableVaga] = useState(false);
    const [enableTipoEstudo, setEnableTipoEstudo] = useState(false);
    const [enableTema, setEnableTema] = useState(false);
    const [enableLinha, setEnableLinha] = useState(false);
    const [enableColuna, setEnableColuna] = useState(false);
    const [enableVagasEvolutivos, setEnableVagasEvolutivos] = useState(false);
    const selectVagasEvolutivosRef = useRef();

    const getStudies = useCallback(async () => {

        try {
            const response = await axiosInstance.post("/tables/getstudygroups",
                {},
                {
                    params: { username, token }
                }
            );
            const result = response.data;

            const newOptions = result.map(element => ({
                value: element,
                label: element
            }));

            setStudiesOptions(newOptions);

        } catch (err) {
            console.error('ERRO [Tables, Navigation, getStudies]: Falha em aceder ao servidor', err);
            if (!err?.response) {
                alert("Falha na comunicação com o servidor. Por favor, contate o serviço técnico da DATA E.");
            }
        }
    }, [username, token]);

    useEffect(() => {
        trackPromise(getStudies());
    }, [getStudies]);

    const handleStudyChange = (selected) => {
        if (!selected) {
            limparVagasEstudo();
            setSelectedStudy(null);
            setStudyName(null);
            return;
        }
        if (DEBUG) {
            console.log("1. [handleStudyChange] Estudo selecionado:", selected);
        }
        setEnableVaga(false);
        setSelectedStudy(selected);
        setStudyName(selected.value);
        limparVagasEstudo();

        // Chamamos a função de buscar vagas
        trackPromise(getWaves(selected.value));
    };

    const getWaves = async (studyName) => {
        if (DEBUG) {
            console.log("2. [getWaves] A disparar pedido para o servidor...");
            console.log("   -> Estudo:", studyName);
            console.log("   -> Username:", username);
            console.log("   -> Token:", token);
        }
        try {
            const response = await axiosInstance.post("/tables/getstudywaves",
                {},
                {
                    params: {
                        username,
                        token,
                        studyName
                    }
                }
            );

            const result = response.data;
            if (DEBUG) {
                console.log("3. [getWaves] Resposta que chegou do Backend:", result);
            }
            const novasVagas = [];
            Object.keys(result).forEach(key => {
                novasVagas.push({ value: key, label: result[key] });
            });
            if (DEBUG) {
                console.log("4. [getWaves] Vagas processadas para a dropdown:", novasVagas);
            }

            setWavesOptions(novasVagas);
            setEnableVaga(true);
        } catch (err) {
            if (DEBUG) {
                console.error('4. [getWaves] OCORREU UM ERRO:', err.message);

                if (err.code === 'ECONNABORTED') {
                    alert("O servidor demorou demasiado tempo a responder (Timeout). Verifique o serviço Java.");
                } else {
                    console.error("Detalhes do erro:", err);
                }
            }
            console.error('ERRO [Tables, Navigation, getWaves]: Falha em aceder ao servidor', err);
            if (!err?.response) {
                alert("Falha na comunicação com o servidor. Por favor, contate o serviço técnico da DATA E.");
            }
        }
    }



    const getStudyTypes = async (waveName) => {
        try {
            const response = await axiosInstance.post("/tables/isevolutive",
                {},
                {
                    params: {
                        username,
                        token,
                        studyName,
                        waveName
                    }
                });

            const result = response.data;
            const newStudyTypes = [
                { value: "simples", label: "Vaga" }
            ];

            if (result) {
                newStudyTypes.push({ value: "evolutivos", label: "Evolutivo" });
            }

            setStudyTypes(newStudyTypes);
            setStudyTypesOptions(newStudyTypes);
            setEnableTipoEstudo(true);


        } catch (err) {
            console.error('ERRO [Tables, Navigation, getStudyTypes]: Falha em aceder ao servidor', err);
            if (!err?.response) {
                alert("Falha na comunicação com o servidor. Por favor, contate o serviço técnico da DATA E.");
            }
        }
    }

    const getThemesOptions = async (isEvolutive) => {
        try {
            const response = await axiosInstance.post("/tables/getthemeoptions",
                {},
                {
                    params: {
                        username,
                        token,
                        studyName,
                        waveName,
                        isEvolutive
                    }
                });

            const result = response.data;
           const newThemes = Object.keys(result).map(key => ({
                value: key,
                label: result[key]
            }));

            setThemes(newThemes);
            setThemesOptions(newThemes);
            setHasThemes(newThemes.length > 0);
            setEnableTema(true);

        } catch (err) {
            console.error('ERRO [Tables, Navigation, getThemesOptions]: Falha em aceder ao servidor', err);
            if (!err?.response) {
                alert("Falha na comunicação com o servidor. Por favor, contate o serviço técnico da DATA E.");
            }
        }
    }

    const handleWaveChange = (selectedWave) => {
        setSelectedWave(selectedWave);
        setWaveName(selectedWave.label);
        trackPromise(getStudyTypes(selectedWave.label));
        limparTipoEstudo();
    };

    const getLineOptions = async (isEvolutivo) => {
        try {
            const response = await axiosInstance.post("/tables/getlineoptions",
                {},
                {
                    params: {
                        username,
                        token,
                        studyName,
                        waveName,
                        isevolutive: isEvolutivo
                    }
                });
            const result = response.data;
            const newLines = Object.keys(result).map(key => ({
                value: key,
                label: result[key]
            }));

            setLines(newLines);
            setLinesOptions(newLines);
            setEnableLinha(true);

        } catch (err) {
            console.error('ERRO [Tables, Navigation, getLineOptions]: Falha em aceder ao servidor', err);
            if (!err?.response) {
                alert("Falha na comunicação com o servidor. Por favor, contate o serviço técnico da DATA E.");
            }
        }

    }

    const handleStudyTypeChange = (selectedStudyType) => {
        setSelectedStudyType(selectedStudyType);
        var evol = false;
        if (selectedStudyType.value === "simples") {
            evol = false;
        } else {
            evol = true;
        }
        limparTemas();
        limparLinhas();
        const promises = [];
        promises.push(getThemesOptions(evol));
        promises.push(getLineOptions(evol));
        Promise.all(promises)
            .then((result) => {
                setIsEvolutivo(evol);
            })
            .catch((error) => {
                console.error('ERRO [Tables, Navigation, handleStudyTypeChange]: Falha ao processar as opções de temas e linhas.', error);
                alert("Falha ao carregar temas e linhas. Por favor, contate o serviço técnico da DATA E.");
            });
    };

    const getLineOptionsTheme = async (isevolutive, theme) => {
        try {
            const response = await axiosInstance.post("/tables/getlineoptionstheme",
                {},
                {
                    params: {
                        username,
                        token,
                        studyName,
                        waveName,
                        theme,
                        isevolutive
                    }
                });
            const result = response.data;
            const newLines = Object.keys(result).map(key => ({
                value: key,
                label: result[key]
            }));

            setLines(newLines);
            setLinesOptions(newLines);
            setEnableLinha(true);

        } catch (err) {
            console.error('ERRO [Tables, Navigation, getLineOptionsTheme]: Falha em aceder ao servidor', err);
            if (!err?.response) {
                alert("Falha na comunicação com o servidor. Por favor, contate o serviço técnico da DATA E.");
            }
        }

    }

    const handleThemeChange = (selectedTheme) => {
        setSelectedTheme(selectedTheme);
        limparLinhas();
        trackPromise(getLineOptionsTheme(isEvolutivo, selectedTheme.value));
    };

    const getColumnOptions = async (lineName) => {
        try {
            const response = await axiosInstance.post("/tables/getcolumnoptions",
                {},
                {
                    params: {
                        username,
                        token,
                        studyName,
                        waveName,
                        lineName
                    }
                });
            const result = response.data;
            const newColumns = Object.keys(result).map(key => ({
                value: key,
                label: result[key]
            }));
            setColumns(newColumns);
            setColumnsOptions(newColumns);
            setEnableColuna(true);
        } catch (err) {
            console.error('ERRO [Tables, Navigation, getColumnOptions]: Falha em aceder ao servidor', err);
            if (!err?.response) {
                alert("Falha na comunicação com o servidor. Por favor, contate o serviço técnico da DATA E.");
            }
        }
    }



    const getVagasEvolutivosOptions = async (lineName) => {
        try {
            const response = await axiosInstance.post("/tables/getvagasevolutivosoptions",
                {},
                {
                    params: {
                        username,
                        token,
                        studyName,
                        waveName,
                        lineName
                    }
                });
            const result = response.data;
            const newVagasEvolutivos = result.map(element => ({
                value: element,
                label: element
            }));

            setVagasEvolutivos(newVagasEvolutivos);
            setVagasEvolutivosOptions(newVagasEvolutivos);
            setEnableVagasEvolutivos(true);
        } catch (err) {
            console.error('ERRO [Tables, Navigation, getVagasEvolutivosOptions]: Falha em aceder ao servidor', err);
            if (!err?.response) {
                alert("Falha na comunicação com o servidor. Por favor, contate o serviço técnico da DATA E.");
            }
        }
    }

    const getAggregatorOptions = async (columnName, isEvolutive, lineNameParam = lineName) => {
        try {
            const response = await axiosInstance.post("/tables/getaggregatoroptions",
                {},
                {
                    params: {
                        username,
                        token,
                        studyName,
                        waveName,
                        lineName: lineNameParam,
                        columnName: columnName || "",
                        isEvolutive
                    }
                });

            const result = response.data;

            const newAggregators = Object.keys(result).map(key => ({
                value: key,
                label: result[key]
            }));

            setAggregators(newAggregators);
            setAggregatorsOptions(newAggregators);

        } catch (err) {
            console.error('ERRO [Tables, Navigation, getAggregatorOptions]: Falha em aceder ao servidor', err);
            if (!err?.response) {
                alert("Falha na comunicação com o servidor. Por favor, contate o serviço técnico da DATA E.");
            }
        }
    }

    const handleLineChange = (selectedLine) => {
        if (!selectedLine) {
            limparColunasVagas();
            setSelectedLine(linesDefaultOption);
            setLineName(null);
            return;
        }

        setSelectedLine(selectedLine);
        setLineName(selectedLine.value);
        limparColunasVagas();

        if (!isEvolutivo) {
            trackPromise(getColumnOptions(selectedLine.value));
        } else {
            trackPromise(getVagasEvolutivosOptions(selectedLine.value));
        }
    };

    const handleColumnChange = (selectedColumn) => {
        limparFiltros();

        if (!selectedColumn) {
            setSelectedColumn(columnsDefaultOption);
            setColumnName(null);
            setColumnLabel(null);
            setShowButtons(false);
            return;
        }

        setSelectedColumn(selectedColumn);
        setColumnName(selectedColumn.value);
        setColumnLabel(selectedColumn.label);
        trackPromise(getAggregatorOptions(selectedColumn.value, false, selectedLine.value));
        setShowButtons(true);
    }

    const handleVagasEvolutivos = (selectedVagaEvolutivosvagasEvolutivos) => {
        limparFiltros();

        const selectedValues = Array.isArray(selectedVagaEvolutivosvagasEvolutivos)
            ? selectedVagaEvolutivosvagasEvolutivos.map(x => x.value)
            : [];

        setSelectedVagasEvolutivo(selectedValues);
        setTotalVagasEvolutivo(selectedValues.length);

        if (selectedValues.length === 0) {
            setShowButtons(false);
            return;
        }

        trackPromise(getAggregatorOptions(null, true, selectedLine.value));
        setShowButtons(true);
    }

    const getAggregatorLineOptions = async (aggregatorName, aggregatorLabel) => {
        try {
            const response = await axiosInstance.post("/tables/getaggregatorlineoptions",
                {},
                {
                    params: {
                        username,
                        token,
                        studyName,
                        waveName,
                        aggregatorName
                    }
                });
            const result = response.data;
            const newAggregatorLines = Object.keys(result).map(key => ({
                value: `aggregatorID: "${aggregatorName}", aggregatorName: "${aggregatorLabel}", aggregatorValue: "${result[key]}"`,
                label: `${aggregatorLabel}: ${result[key]}`
            }));

            setAggregatorLines(newAggregatorLines);
            setAggregatorsLineOptions(newAggregatorLines);

        } catch (err) {
            console.error('ERRO [Tables, Navigation, getAggregatorLineOptions]: Falha em aceder ao servidor', err);
            if (!err?.response) {
                alert("Falha na comunicação com o servidor. Por favor, contate o serviço técnico da DATA E.");
            }
        }
    }

    const handleAggregatorChange = (selectedAggregator) => {
        setSelectedAggregator(selectedAggregator || aggregatorsDefaultOption);
        setAggregatorsLineOptions([]);
        setFilters([]);

        if (!selectedAggregator) {
            return;
        }

        trackPromise(getAggregatorLineOptions(selectedAggregator.value, selectedAggregator.label));
    }

    const handleAggregatorLineChange = (selectedAggregatorLine) => {
        const value = Array.isArray(selectedAggregatorLine)
            ? selectedAggregatorLine.map(x => x.value)
            : [];

        setFilters(value);
    }

    const limparVagasEstudo = () => {
        setWavesOptions([]);
        setSelectedWave(wavesDefaultOption);
        limparTipoEstudo();
        setIsEvolutivo(false);
        setEnableVaga(false);
    }

    const limparVariaveis = () => {
        setSelectedStudyType(studyTypesDefaultOption);
        if (hasThemes) {
            limparTemas();
        } else {
            limparLinhas();
        }
        setHasThemes(false);
    }

    const limparTipoEstudo = () => {
        setStudyTypesOptions([]);
        limparVariaveis();
        setEnableTipoEstudo(false);
    }

    const limparTemas = () => {
        setThemesOptions([]);
        setSelectedTheme(themesDefaultOption);
        setHasThemes(false);
        limparLinhas();
        setEnableTema(false);
    }

    const limparLinhas = () => {
        setLinesOptions([]);
        setSelectedLine(linesDefaultOption);
        limparColunasVagas();
        setEnableLinha(false);
    }

    const limparColunasVagas = () => {
        limparFiltros();
        if (!isEvolutivo) {
            setColumnsOptions([]);
            setSelectedColumn(columnsDefaultOption);
            setEnableColuna(false);
        } else {
            setVagasEvolutivosOptions([]);
            setVagasEvolutivos([]);
            setTotalVagasEvolutivo(0);
            setSelectedVagasEvolutivo([]);
            if (selectVagasEvolutivosRef.current) {
                selectVagasEvolutivosRef.current.clearValue();
            }
            setEnableVagasEvolutivos(false);
        }
        setShowButtons(false);
    }

    const limparFiltros = () => {
        setAggregatorsOptions([]);
        setSelectedAggregator(aggregatorsDefaultOption);
        setAggregatorsLineOptions([]);
        setFilters([]);
        setShowFilter(false);
        setShowSimpleStudy(false);
        setShowEvolutiveStudy(false);
    }

    const handleLimparVariaveis = () => {
        limparVariaveis();
        setShowSimpleStudy(false);
        setShowEvolutiveStudy(false);
    }

    const handleMostrarResultados = () => {
        setShowSimpleStudy(false);
        setShowEvolutiveStudy(false);
        if (!isEvolutivo) {
            setShowSimpleStudy(true);
        } else {
            setShowEvolutiveStudy(true);
        }
    }

    return (
        <div style={{ padding: 10, color: "white", backgroundColor: "#6B747F" }}>
            <table style={{ width: "100%" }}>
                <tbody>
                    <tr>
                        <td style={{ width: "20%", verticalAlign: "top", paddingTop: "2px", paddingBottom: "2px" }}>
                            <nav>
                                <ul>
                                    <div style={{ padding: 5 }}>
                                        <h6>Estudo</h6>
                                        <Select
                                            placeholder="Estudo"
                                            onChange={handleStudyChange}
                                            value={selectedStudy}
                                            options={studiesOptions}
                                            styles={colourStyles}
                                            isClearable={false}
                                        />
                                    </div>
                                    <div style={{ padding: 5 }}>
                                        <h6>Vaga</h6>
                                        <Select
                                            isDisabled={!enableVaga}
                                            placeholder="Vaga"
                                            value={selectedWave}
                                            onChange={handleWaveChange}
                                            options={wavesOptions}
                                            styles={colourStyles}
                                            cacheOptions={false}
                                        />
                                    </div>
                                    <div style={{ padding: 5 }}>
                                        <h6>Tipo de estudo</h6>
                                        <Select
                                            isDisabled={!enableTipoEstudo}
                                            placeholder="Tipo"
                                            value={selectedStudyType}
                                            onChange={handleStudyTypeChange}
                                            options={studyTypesOptions}
                                            styles={colourStyles}
                                            cacheOptions={false}
                                        />
                                    </div>
                                    {hasThemes &&
                                        <div style={{ padding: 5 }}>
                                            <h6>Tema</h6>
                                            <Select
                                                isDisabled={!enableTema}
                                                placeholder="Tema"
                                                value={selectedTheme}
                                                onChange={handleThemeChange}
                                                options={themesOptions}
                                                styles={colourStyles}
                                                cacheOptions={false}
                                            />
                                        </div>
                                    }
                                    <div style={{ padding: 5 }}>
                                        <h6>Variável em linha</h6>
                                        <Select
                                            isDisabled={!enableLinha}
                                            placeholder="Variável em linha"
                                            value={selectedLine}
                                            onChange={handleLineChange}
                                            options={linesOptions}
                                            styles={colourStyles}
                                            cacheOptions={false}
                                        />
                                    </div>
                                    {!isEvolutivo &&
                                        <div style={{ padding: 5 }}>
                                            <h6>Variável em coluna</h6>
                                            <Select
                                                isDisabled={!enableColuna}
                                                placeholder="Variável em coluna"
                                                value={selectedColumn}
                                                onChange={handleColumnChange}
                                                options={columnsOptions}
                                                styles={colourStyles}
                                                cacheOptions={false}
                                            />
                                        </div>}
                                    {isEvolutivo &&
                                        <div style={{ padding: 5 }}>
                                            <h6>Vagas para evolutivos</h6>
                                            <Select
                                                ref={selectVagasEvolutivosRef}
                                                isDisabled={!enableVagasEvolutivos}
                                                onChange={handleVagasEvolutivos}
                                                placeholder="Vagas para evolutivos"
                                                isMulti={true}
                                                closeMenuOnSelect={false}
                                                components={animatedComponents}
                                                options={totalVagasEvolutivo === maxVagasEvolutivos ? [] : vagasEvolutivosOptions}
                                                noOptionsMessage={() => {
                                                    return totalVagasEvolutivo === maxVagasEvolutivos
                                                        ? 'Só pode selecionar até 7 vagas'
                                                        : 'Não existem mais vagas para selecionar';
                                                }}
                                                styles={colourStyles}
                                                cacheOptions={false}
                                            />
                                            <span style={{ fontSize: 10 }}>* Selecione no máximo 7 vagas.</span>
                                        </div>}
                                    {showFilter && <div style={{ padding: 5 }}>
                                        <h5><BsFilterCircleFill />&nbsp;Filtros</h5>
                                    </div>}
                                    {showFilter &&
                                        <div style={{ padding: 5 }}>
                                            <h6>Variáveis</h6>
                                            <Select
                                                value={selectedAggregator}
                                                options={aggregatorsOptions}
                                                onChange={handleAggregatorChange}
                                                placeholder="variáveis para filtros"
                                                styles={colourStyles}
                                                cacheOptions={false}
                                            />
                                        </div>}
                                    {showFilter &&
                                        <div style={{ padding: 5 }}>
                                            <h6>Opções de filtragem</h6>
                                            <Select
                                                options={aggregatorLineOptions}
                                                closeMenuOnSelect={false}
                                                isMulti={true}
                                                components={animatedComponents}
                                                onChange={handleAggregatorLineChange}
                                                placeholder="opções de filtragem"
                                                styles={colourStyles}
                                                cacheOptions={false}
                                                noOptionsMessage={() => {
                                                    return 'Não mais existem opções';
                                                }}
                                            />
                                        </div>
                                    }
                                    {
                                        showButtons &&
                                        <div>
                                            <div align="center">
                                                <button style={{ width: "170px", height: "50px", fontSize: "14px" }} onClick={handleMostrarResultados}><BsFileEarmarkBarGraphFill />&nbsp;Mostrar resultados</button>
                                            </div>
                                            <div align="center">
                                                <button style={{ width: "170px", height: "50px", fontSize: "14px" }} onClick={() => setShowFilter(showFilter => !showFilter)}><BsFilter />&nbsp;{!showFilter ? 'Mostrar filtros' : 'Ocultar filtros'} </button>
                                            </div>

                                        </div>
                                    }

                                    <div align="center">
                                        <button style={{ width: "170px", height: "50px", fontSize: "14px" }} onClick={handleLimparVariaveis}><BsFillTrashFill />&nbsp;Limpar variáveis</button>
                                    </div>


                                </ul>
                            </nav>
                        </td>

                        <td colSpan={2} style={{ width: "100%", verticalAlign: "top", paddingTop: "2px", paddingBottom: "2px" }}>
                            {showSimpleStudy && <div>
                                <SimpleStudytData
                                    username={username}
                                    token={token}
                                    studyName={studyName}
                                    waveName={waveName}
                                    lineName={lineName}
                                    columnName={columnName}
                                    columnLabel={columnLabel}
                                    filters={filters}
                                    authContext={authContext}
                                />
                            </div>}
                            {showEvolutiveStudy && <div>
                                <EvolutiveStudytData
                                    username={username}
                                    token={token}
                                    studyName={studyName}
                                    waveID={selectedWave?.value}
                                    waveName={waveName}
                                    lineName={lineName}
                                    selectedVagasEvolutivo={selectedVagasEvolutivo}
                                    filters={filters}
                                />
                            </div>}

                            {/* AQUI ESTÁ A CORREÇÃO BLINDADA COM O ?. */}
                            {!showSimpleStudy && !showEvolutiveStudy && (!selectedStudy || selectedStudy?.value === "") && <div>
                                <TablesNoData
                                    title1=""
                                    title2="Benvindo ao SoftData Tables."
                                    title3="Por favor selecione o estudo no canto superior esquerdo." />
                            </div>}
                            {!showSimpleStudy && !showEvolutiveStudy && selectedStudy?.value !== "" && selectedStudy && <div>
                                <TablesNoData
                                    title1=""
                                    title2=""
                                    title3="" />
                            </div>}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};
export default Navigation;